/**
 * Generated by orval 🍺
 * Do not edit manually.
 * VMS Database Service
 * The cloud's database of organizations, their members, servers, cameras, rules and the rest
 * of the configuration that media_servers synchronize.
 *
 * The service validates every request that this document describes against it. A request
 * that does not match gets a 400 with an `Error` body.
 *
 * Each operation names who may call it in `x-access`, and the service registers the
 * operation behind the middleware of that chain:
 * - `public`: anyone. The paths under `/internal` are public too: the ingress does not
 *   forward them, so only the services of the cluster reach them.
 * - `login`: a person with a Google ID token or a cloud session.
 * - `authenticated`: a holder of a cloud token, a user's session or a server's machine
 *   token. The request names no organization, and the operation answers with what belongs
 *   to the caller.
 * - `org-scoped`: the same caller, a member of the organization in the `orgId` query
 *   parameter, with a role that allows the operation.
 * - `platform-admin`: the same caller, an operator of the deployment.
 *
 * OpenAPI spec version: 1.0.0
 */
import * as zod from 'zod';

export const Error = zod.object({
  "error": zod.string()
});

export type Error = zod.input<typeof Error>;
export type ErrorOutput = zod.output<typeof Error>;

export const AccessibleResponse = zod.object({
  "unrestricted": zod.boolean(),
  "ids": zod.array(zod.string()).nullable()
});

export type AccessibleResponse = zod.input<typeof AccessibleResponse>;
export type AccessibleResponseOutput = zod.output<typeof AccessibleResponse>;

export const Agent = zod.object({
  "manifest": zod.unknown(),
  "settings": zod.record(zod.string(), zod.string()).optional(),
  "serverId": zod.string().optional()
});

export type Agent = zod.input<typeof Agent>;
export type AgentOutput = zod.output<typeof Agent>;

export const AgentInput = zod.object({
  "manifest": zod.unknown().optional(),
  "settings": zod.record(zod.string(), zod.string().nullable()).nullish(),
  "serverId": zod.string().nullish()
});

export type AgentInput = zod.input<typeof AgentInput>;
export type AgentInputOutput = zod.output<typeof AgentInput>;

export const AuditRef = zod.object({
  "id": zod.string(),
  "name": zod.string().optional()
});

export type AuditRef = zod.input<typeof AuditRef>;
export type AuditRefOutput = zod.output<typeof AuditRef>;

export const AuditDetails = zod.object({
  "name": zod.string().optional(),
  "keys": zod.array(zod.string()).optional(),
  "version": zod.string().optional(),
  "devices": zod.array(AuditRef).optional(),
  "rangeStartMs": zod.int().optional(),
  "rangeEndMs": zod.int().optional(),
  "peerAddress": zod.string().optional(),
  "userAgent": zod.string().optional(),
  "count": zod.int().optional(),
  "replacement": AuditRef.optional(),
  "targetServer": AuditRef.optional(),
  "serverId": zod.string().optional().describe('The server an object belongs to, for one reached through its server.'),
  "text": zod.string().optional().describe('What the user asked the AI chat.'),
  "description": zod.string().optional().describe('What the object does, for one that says it, as a rule\'s events and actions.'),
  "port": zod.int().optional().describe('The output of an I/O module that a press or a hold was about, 1-based.'),
  "portName": zod.string().optional().describe('The name that the output had at the time.'),
  "changes": zod.string().optional().describe('What a port configuration change changed, in one line.')
});

export type AuditDetails = zod.input<typeof AuditDetails>;
export type AuditDetailsOutput = zod.output<typeof AuditDetails>;

export const AuditRefInput = zod.object({
  "id": zod.string().nullish(),
  "name": zod.string().nullish()
});

export type AuditRefInput = zod.input<typeof AuditRefInput>;
export type AuditRefInputOutput = zod.output<typeof AuditRefInput>;

export const AuditDetailsInput = zod.object({
  "name": zod.string().nullish(),
  "keys": zod.array(zod.string().nullable()).nullish(),
  "version": zod.string().nullish(),
  "devices": zod.array(AuditRefInput).nullish(),
  "rangeStartMs": zod.int().nullish(),
  "rangeEndMs": zod.int().nullish(),
  "peerAddress": zod.string().nullish(),
  "userAgent": zod.string().nullish(),
  "count": zod.int().nullish(),
  "replacement": zod.union([AuditRefInput,zod.null()]).optional(),
  "targetServer": zod.union([AuditRefInput,zod.null()]).optional(),
  "serverId": zod.string().nullish(),
  "text": zod.string().nullish(),
  "description": zod.string().nullish(),
  "port": zod.int().nullish(),
  "portName": zod.string().nullish(),
  "changes": zod.string().nullish()
});

export type AuditDetailsInput = zod.input<typeof AuditDetailsInput>;
export type AuditDetailsInputOutput = zod.output<typeof AuditDetailsInput>;

export const DeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const DeviceId = zod.string().regex(DeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.');

export type DeviceId = zod.input<typeof DeviceId>;
export type DeviceIdOutput = zod.output<typeof DeviceId>;

export const AuditRecord = zod.object({
  "id": zod.string(),
  "seq": zod.int().describe('The read cursor, the position in the cloud\'s trail, newest last.'),
  "type": zod.enum(['login', 'unauthorizedLogin', 'view', 'export', 'device', 'deviceReplacement', 'server', 'serverReplacement', 'storage', 'user', 'rule', 'bookmark', 'case', 'settings', 'update', 'chat', 'ioOutputPress', 'ioOutputHold', 'ioPortConfig']),
  "action": zod.enum(['insert', 'update', 'remove']).optional(),
  "actor": zod.string().describe('An email, `admin` for a server\'s local admin, or the name a refused login tried.'),
  "sessionId": zod.string().optional().describe('The login session\'s id, for everything a cloud user does in one sign-in.'),
  "serverId": zod.string().optional().describe('The media_server that recorded it; absent for the cloud\'s own records.'),
  "resourceId": zod.string().optional().describe('The object the action was about, of the kind that `type` says, as a device id for a camera or an I/O module.'),
  "eventAtMs": zod.int(),
  "durationMs": zod.int().optional(),
  "details": AuditDetails,
  "deviceIds": zod.array(DeviceId).optional().describe('Every device the record is about.')
});

export type AuditRecord = zod.input<typeof AuditRecord>;
export type AuditRecordOutput = zod.output<typeof AuditRecord>;

export const AuditRecordInput = zod.object({
  "id": zod.string().nullish(),
  "seq": zod.int().nullish(),
  "type": zod.string().nullish(),
  "action": zod.string().nullish(),
  "actor": zod.string().nullish(),
  "sessionId": zod.string().nullish(),
  "serverId": zod.string().nullish(),
  "resourceId": zod.string().nullish(),
  "eventAtMs": zod.int().nullish(),
  "durationMs": zod.int().nullish(),
  "details": zod.union([AuditDetailsInput,zod.null()]).optional(),
  "deviceIds": zod.array(zod.string().nullable()).nullish()
});

export type AuditRecordInput = zod.input<typeof AuditRecordInput>;
export type AuditRecordInputOutput = zod.output<typeof AuditRecordInput>;

export const UpdateCandidate = zod.object({
  "version": zod.string(),
  "changeSet": zod.string(),
  "publishedAt": zod.iso.datetime({"offset":true}),
  "serversBehind": zod.int()
});

export type UpdateCandidate = zod.input<typeof UpdateCandidate>;
export type UpdateCandidateOutput = zod.output<typeof UpdateCandidate>;

export const BuildArtifact = zod.object({
  "platform": zod.string(),
  "url": zod.string(),
  "sizeBytes": zod.int(),
  "sha256": zod.string()
});

export type BuildArtifact = zod.input<typeof BuildArtifact>;
export type BuildArtifactOutput = zod.output<typeof BuildArtifact>;

export const MaintenanceWindow = zod.object({
  "start": zod.string(),
  "end": zod.string()
});

export type MaintenanceWindow = zod.input<typeof MaintenanceWindow>;
export type MaintenanceWindowOutput = zod.output<typeof MaintenanceWindow>;

export const UpdatePlan = zod.object({
  "id": zod.string(),
  "targetVersion": zod.string(),
  "artifacts": zod.array(BuildArtifact),
  "maintenanceWindow": MaintenanceWindow,
  "installNow": zod.boolean(),
  "plannedBy": zod.string(),
  "plannedAt": zod.iso.datetime({"offset":true}),
  "cancelledAt": zod.iso.datetime({"offset":true}).optional(),
  "cancelReason": zod.enum(['admin', 'withdrawn']).optional(),
  "completedAt": zod.iso.datetime({"offset":true}).optional(),
  "privateBuildKey": zod.string().optional().describe('The key of the private build that the plan installs.'),
  "state": zod.enum(['planned', 'ready', 'installing', 'complete', 'cancelled']).optional()
});

export type UpdatePlan = zod.input<typeof UpdatePlan>;
export type UpdatePlanOutput = zod.output<typeof UpdatePlan>;

export const AvailableUpdates = zod.object({
  "candidate": zod.union([UpdateCandidate,zod.null()]),
  "plan": zod.union([UpdatePlan,zod.null()]),
  "maintenanceWindow": zod.union([MaintenanceWindow,zod.null()]).describe('The window last planned, to prefill the next plan.')
});

export type AvailableUpdates = zod.input<typeof AvailableUpdates>;
export type AvailableUpdatesOutput = zod.output<typeof AvailableUpdates>;

export const BinaryFile = zod.object({
  "id": zod.string(),
  "name": zod.string(),
  "contentType": zod.string(),
  "size": zod.int(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "createdBy": zod.string().optional(),
  "usedByRules": zod.array(zod.string()).optional().describe('The names of the rules that play this file; a file in use cannot be deleted.')
});

export type BinaryFile = zod.input<typeof BinaryFile>;
export type BinaryFileOutput = zod.output<typeof BinaryFile>;

export const Bookmark = zod.object({
  "id": zod.string(),
  "deviceId": DeviceId,
  "startTimeMs": zod.int(),
  "durationMs": zod.int(),
  "tags": zod.array(zod.string()),
  "description": zod.string(),
  "protectArchive": zod.boolean().describe('The owning server\'s archive cleaner never removes a segment overlapping the span. Setting or changing it needs the administrator role.'),
  "createdAt": zod.iso.datetime({"offset":true}),
  "updatedAt": zod.iso.datetime({"offset":true})
});

export type Bookmark = zod.input<typeof Bookmark>;
export type BookmarkOutput = zod.output<typeof Bookmark>;

export const BookmarkInput = zod.object({
  "id": zod.string().nullish(),
  "deviceId": zod.string().nullish(),
  "startTimeMs": zod.int().nullish(),
  "durationMs": zod.int().nullish(),
  "tags": zod.array(zod.string().nullable()).nullish(),
  "description": zod.string().nullish(),
  "protectArchive": zod.boolean().nullish(),
  "createdAt": zod.iso.datetime({"offset":true}).nullish(),
  "updatedAt": zod.iso.datetime({"offset":true}).nullish()
});

export type BookmarkInput = zod.input<typeof BookmarkInput>;
export type BookmarkInputOutput = zod.output<typeof BookmarkInput>;

export const BookmarkTag = zod.object({
  "name": zod.string(),
  "bookmarkCount": zod.int()
});

export type BookmarkTag = zod.input<typeof BookmarkTag>;
export type BookmarkTagOutput = zod.output<typeof BookmarkTag>;

export const Build = zod.object({
  "version": zod.string(),
  "changeSet": zod.string(),
  "publishedAt": zod.iso.datetime({"offset":true}),
  "artifacts": zod.array(BuildArtifact),
  "registeredBy": zod.string(),
  "registeredAt": zod.iso.datetime({"offset":true}),
  "withdrawnAt": zod.iso.datetime({"offset":true}).optional().describe('Set once the build is no longer offered; the row itself stays.'),
  "archivedAt": zod.iso.datetime({"offset":true}).optional().describe('Set once the build\'s files may be gone from the release bucket; the row itself stays.')
});

export type Build = zod.input<typeof Build>;
export type BuildOutput = zod.output<typeof Build>;

export const Case = zod.object({
  "id": zod.string(),
  "number": zod.string(),
  "title": zod.string(),
  "description": zod.string(),
  "status": zod.enum(['open', 'review', 'closed']),
  "ownerEmail": zod.string(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "updatedAt": zod.iso.datetime({"offset":true})
});

export type Case = zod.input<typeof Case>;
export type CaseOutput = zod.output<typeof Case>;

export const CaseAttachment = zod.object({
  "id": zod.string(),
  "caseId": zod.string(),
  "kind": zod.enum(['image', 'video', 'document']),
  "name": zod.string(),
  "fileName": zod.string(),
  "mimeType": zod.string(),
  "sizeBytes": zod.int(),
  "note": zod.string(),
  "addedBy": zod.string(),
  "addedAt": zod.iso.datetime({"offset":true}),
  "updatedAt": zod.iso.datetime({"offset":true})
});

export type CaseAttachment = zod.input<typeof CaseAttachment>;
export type CaseAttachmentOutput = zod.output<typeof CaseAttachment>;

export const CaseDetail = zod.object({
  "id": zod.string(),
  "number": zod.string(),
  "title": zod.string(),
  "description": zod.string(),
  "status": zod.enum(['open', 'review', 'closed']),
  "ownerEmail": zod.string(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "updatedAt": zod.iso.datetime({"offset":true}),
  "attachments": zod.array(CaseAttachment),
  "bookmarks": zod.array(Bookmark)
});

export type CaseDetail = zod.input<typeof CaseDetail>;
export type CaseDetailOutput = zod.output<typeof CaseDetail>;

export const CaseInput = zod.object({
  "id": zod.string().nullish(),
  "number": zod.string().nullish(),
  "title": zod.string().nullish(),
  "description": zod.string().nullish(),
  "status": zod.string().nullish(),
  "ownerEmail": zod.string().nullish(),
  "createdAt": zod.iso.datetime({"offset":true}).nullish(),
  "updatedAt": zod.iso.datetime({"offset":true}).nullish()
});

export type CaseInput = zod.input<typeof CaseInput>;
export type CaseInputOutput = zod.output<typeof CaseInput>;

export const ChangeEvent = zod.object({
  "seq": zod.int().optional(),
  "action": zod.string(),
  "resource": zod.string(),
  "orgId": zod.string().optional(),
  "serverId": zod.string().optional(),
  "id": zod.string(),
  "data": zod.unknown().optional(),
  "origin": zod.string().optional(),
  "prevSeq": zod.int().optional()
});

export type ChangeEvent = zod.input<typeof ChangeEvent>;
export type ChangeEventOutput = zod.output<typeof ChangeEvent>;

export const ChannelPartner = zod.object({
  "id": zod.string(),
  "name": zod.string()
});

export type ChannelPartner = zod.input<typeof ChannelPartner>;
export type ChannelPartnerOutput = zod.output<typeof ChannelPartner>;

export const ChannelPartnerInput = zod.object({
  "id": zod.string().nullish(),
  "name": zod.string().nullish()
});

export type ChannelPartnerInput = zod.input<typeof ChannelPartnerInput>;
export type ChannelPartnerInputOutput = zod.output<typeof ChannelPartnerInput>;

export const ChatRequest = zod.object({
  "orgId": zod.string().nullish(),
  "email": zod.string().nullish(),
  "sessionId": zod.string().nullish(),
  "text": zod.string().nullish()
});

export type ChatRequest = zod.input<typeof ChatRequest>;
export type ChatRequestOutput = zod.output<typeof ChatRequest>;

export const CheckResponse = zod.object({
  "allowed": zod.boolean(),
  "reason": zod.string(),
  "nextOpen": zod.string().optional()
});

export type CheckResponse = zod.input<typeof CheckResponse>;
export type CheckResponseOutput = zod.output<typeof CheckResponse>;

export const ClaimRequest = zod.object({
  "code": zod.string().nullish(),
  "orgId": zod.string().nullish()
});

export type ClaimRequest = zod.input<typeof ClaimRequest>;
export type ClaimRequestOutput = zod.output<typeof ClaimRequest>;

export const ClaimResponse = zod.object({
  "serverId": zod.string(),
  "orgId": zod.string(),
  "serverName": zod.string(),
  "kind": zod.enum(['media_server', 'ai_agent_toolbox']).optional()
});

export type ClaimResponse = zod.input<typeof ClaimResponse>;
export type ClaimResponseOutput = zod.output<typeof ClaimResponse>;

export const ClientConfig = zod.object({
  "googleClientId": zod.string()
});

export type ClientConfig = zod.input<typeof ClientConfig>;
export type ClientConfigOutput = zod.output<typeof ClientConfig>;

export const MaintenanceWindowInput = zod.object({
  "start": zod.string().nullish(),
  "end": zod.string().nullish()
});

export type MaintenanceWindowInput = zod.input<typeof MaintenanceWindowInput>;
export type MaintenanceWindowInputOutput = zod.output<typeof MaintenanceWindowInput>;

export const CreatePrivateUpdatePlanRequest = zod.object({
  "key": zod.string().nullish().describe('The key of the private build: 6 symbols from A-Z without O and I, and from 2-9.'),
  "maintenanceWindow": zod.union([MaintenanceWindowInput,zod.null()]).optional(),
  "installNow": zod.boolean().nullish()
});

export type CreatePrivateUpdatePlanRequest = zod.input<typeof CreatePrivateUpdatePlanRequest>;
export type CreatePrivateUpdatePlanRequestOutput = zod.output<typeof CreatePrivateUpdatePlanRequest>;

export const CreateUpdatePlanRequest = zod.object({
  "version": zod.string().nullish(),
  "maintenanceWindow": zod.union([MaintenanceWindowInput,zod.null()]).optional(),
  "installNow": zod.boolean().nullish()
});

export type CreateUpdatePlanRequest = zod.input<typeof CreateUpdatePlanRequest>;
export type CreateUpdatePlanRequestOutput = zod.output<typeof CreateUpdatePlanRequest>;

export const DeleteResponse = zod.object({
  "deletedOrg": zod.boolean(),
  "orgId": zod.string()
});

export type DeleteResponse = zod.input<typeof DeleteResponse>;
export type DeleteResponseOutput = zod.output<typeof DeleteResponse>;

export const DeviceBase = zod.object({
  "id": DeviceId,
  "url": zod.string(),
  "model": zod.string(),
  "manufacturer": zod.string(),
  "firmware": zod.string(),
  "driverType": zod.string(),
  "user": zod.string(),
  "password": zod.string(),
  "mac": zod.string().describe('The hardware address in 12 lowercase hex digits; empty when the driver has not learned it.'),
  "webUrl": zod.string().describe('The camera\'s own web page; empty for a camera that serves none, such as a bare RTSP one.'),
  "deviceType": zod.enum(['camera', 'io']).describe('What the device is. A media_server older than the type sends none, which meant a camera; the cloud stores that as `camera`.')
});

export type DeviceBase = zod.input<typeof DeviceBase>;
export type DeviceBaseOutput = zod.output<typeof DeviceBase>;

export const IoPortConfig = zod.object({
  "port": zod.int(),
  "direction": zod.enum(['input', 'output', 'off']).describe('What the port is used for; an `off` port is neither read nor driven.'),
  "name": zod.string(),
  "normalState": zod.enum(['open', 'closed']).describe('The level the port rests at; the opposite level is active.'),
  "pulseMs": zod.int().describe('How long an output stays active when pressed.'),
  "cooldownMs": zod.int().describe('The minimum time between two activations of an output; 0 is none.')
}).describe('One port of an I/O module, numbered 1..8, as configured.');

export type IoPortConfig = zod.input<typeof IoPortConfig>;
export type IoPortConfigOutput = zod.output<typeof IoPortConfig>;

export const Device = zod.object({
  "base": DeviceBase,
  "name": zod.string(),
  "serverId": zod.string(),
  "primaryUrl": zod.string(),
  "secondaryUrl": zod.string(),
  "recordingEnabled": zod.boolean(),
  "enabledAgents": zod.unknown().optional(),
  "blurCategories": zod.unknown().optional(),
  "params": zod.unknown().optional(),
  "recordingSettings": zod.unknown().optional(),
  "primaryStream": zod.unknown().optional(),
  "secondaryStream": zod.unknown().optional(),
  "availableProfiles": zod.unknown().optional(),
  "primaryProfileToken": zod.string().optional(),
  "secondaryProfileToken": zod.string().optional(),
  "optimizeSettings": zod.boolean(),
  "rotationDegrees": zod.union([zod.literal(0),zod.literal(90),zod.literal(180),zod.literal(270)]).optional(),
  "audioEnabled": zod.boolean().optional(),
  "hasTwoWayAudio": zod.boolean().optional().describe('Whether the device offers a two-way audio backchannel, probed by its driver.'),
  "talkEnabled": zod.boolean().optional().describe('Whether the talk button is shown while viewing this camera. Unset, it follows hasTwoWayAudio; once set, it is sticky.'),
  "talkTargets": zod.unknown().optional(),
  "replacement": zod.unknown().optional(),
  "capabilities": zod.array(zod.string()).optional().describe('What the device can do, as its driver learned it: `video`, `audioOut`, `audioIn`, `io`.'),
  "ioPorts": zod.array(IoPortConfig).optional().describe('The port configuration of an I/O module, as the module holds it. It changes through `PUT /api/v1/devices/{deviceId}/io/ports` on the media_server.')
});

export type Device = zod.input<typeof Device>;
export type DeviceOutput = zod.output<typeof Device>;

export const DeviceAgent = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string(),
  "serverId": zod.string(),
  "manifest": zod.unknown().optional(),
  "settings": zod.unknown().optional()
});

export type DeviceAgent = zod.input<typeof DeviceAgent>;
export type DeviceAgentOutput = zod.output<typeof DeviceAgent>;

export const DeviceAgentInput = zod.object({
  "agentId": zod.string().nullish(),
  "deviceId": zod.string().nullish(),
  "serverId": zod.string().nullish(),
  "manifest": zod.unknown().optional(),
  "settings": zod.unknown().optional()
});

export type DeviceAgentInput = zod.input<typeof DeviceAgentInput>;
export type DeviceAgentInputOutput = zod.output<typeof DeviceAgentInput>;

export const DeviceBaseInput = zod.object({
  "id": zod.string().nullish(),
  "url": zod.string().nullish(),
  "model": zod.string().nullish(),
  "manufacturer": zod.string().nullish(),
  "firmware": zod.string().nullish(),
  "driverType": zod.string().nullish(),
  "user": zod.string().nullish(),
  "password": zod.string().nullish(),
  "mac": zod.string().nullish(),
  "webUrl": zod.string().nullish(),
  "deviceType": zod.string().nullish()
});

export type DeviceBaseInput = zod.input<typeof DeviceBaseInput>;
export type DeviceBaseInputOutput = zod.output<typeof DeviceBaseInput>;

export const DeviceInput = zod.object({
  "base": zod.union([DeviceBaseInput,zod.null()]).optional(),
  "name": zod.string().nullish(),
  "serverId": zod.string().nullish(),
  "primaryUrl": zod.string().nullish(),
  "secondaryUrl": zod.string().nullish(),
  "recordingEnabled": zod.boolean().nullish(),
  "enabledAgents": zod.unknown().optional(),
  "blurCategories": zod.unknown().optional(),
  "params": zod.unknown().optional(),
  "recordingSettings": zod.unknown().optional(),
  "primaryStream": zod.unknown().optional(),
  "secondaryStream": zod.unknown().optional(),
  "availableProfiles": zod.unknown().optional(),
  "primaryProfileToken": zod.string().nullish(),
  "secondaryProfileToken": zod.string().nullish(),
  "optimizeSettings": zod.boolean().nullish(),
  "rotationDegrees": zod.int().nullish(),
  "audioEnabled": zod.boolean().nullish(),
  "hasTwoWayAudio": zod.boolean().nullish(),
  "talkEnabled": zod.boolean().nullish(),
  "talkTargets": zod.unknown().optional(),
  "replacement": zod.unknown().optional(),
  "capabilities": zod.unknown().optional(),
  "ioPorts": zod.unknown().optional()
});

export type DeviceInput = zod.input<typeof DeviceInput>;
export type DeviceInputOutput = zod.output<typeof DeviceInput>;

export const EnrollImageInput = zod.object({
  "data": zod.string().nullish(),
  "mimeType": zod.string().nullish()
});

export type EnrollImageInput = zod.input<typeof EnrollImageInput>;
export type EnrollImageInputOutput = zod.output<typeof EnrollImageInput>;

export const EnrollExemplarInput = zod.object({
  "eventId": zod.string().nullish(),
  "deviceId": zod.string().nullish(),
  "categoryClass": zod.string().nullish(),
  "embedding": zod.array(zod.number().nullable()).nullish(),
  "image": zod.union([EnrollImageInput,zod.null()]).optional()
});

export type EnrollExemplarInput = zod.input<typeof EnrollExemplarInput>;
export type EnrollExemplarInputOutput = zod.output<typeof EnrollExemplarInput>;

export const EnrollRequest = zod.object({
  "name": zod.string().nullish(),
  "negative": zod.boolean().nullish(),
  "exemplars": zod.array(EnrollExemplarInput).nullish()
});

export type EnrollRequest = zod.input<typeof EnrollRequest>;
export type EnrollRequestOutput = zod.output<typeof EnrollRequest>;

export const EnrollResponse = zod.object({
  "enrollmentId": zod.string(),
  "name": zod.string(),
  "categoryClass": zod.string(),
  "negative": zod.boolean(),
  "exemplarCount": zod.int(),
  "added": zod.int(),
  "skippedDuplicates": zod.int(),
  "skippedConflicts": zod.int(),
  "skippedNoEmbedding": zod.int(),
  "skippedNoImage": zod.int()
});

export type EnrollResponse = zod.input<typeof EnrollResponse>;
export type EnrollResponseOutput = zod.output<typeof EnrollResponse>;

export const Enrollment = zod.object({
  "enrollmentId": zod.string(),
  "name": zod.string(),
  "categoryClass": zod.string(),
  "color": zod.string().optional().describe('An `#rrggbb` colour chosen by an operator; absent means none.'),
  "exemplarCount": zod.int(),
  "negativeExemplarCount": zod.int(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "updatedAt": zod.iso.datetime({"offset":true})
});

export type Enrollment = zod.input<typeof Enrollment>;
export type EnrollmentOutput = zod.output<typeof Enrollment>;

export const EnrollmentExemplar = zod.object({
  "exemplarId": zod.string(),
  "enrollmentId": zod.string(),
  "sourceEventId": zod.string(),
  "deviceId": zod.string().optional(),
  "categoryClass": zod.string().optional(),
  "embedding": zod.array(zod.number()).optional().describe('Only when the read asked for the vectors.'),
  "model": zod.string().optional(),
  "mimeType": zod.string().optional(),
  "negative": zod.boolean().optional().describe('Sent only for a counter-example.'),
  "createdAt": zod.iso.datetime({"offset":true})
});

export type EnrollmentExemplar = zod.input<typeof EnrollmentExemplar>;
export type EnrollmentExemplarOutput = zod.output<typeof EnrollmentExemplar>;

export const EnrollmentDetail = zod.object({
  "enrollmentId": zod.string(),
  "name": zod.string(),
  "categoryClass": zod.string(),
  "color": zod.string().optional(),
  "exemplarCount": zod.int(),
  "negativeExemplarCount": zod.int(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "updatedAt": zod.iso.datetime({"offset":true}),
  "exemplars": zod.array(EnrollmentExemplar)
});

export type EnrollmentDetail = zod.input<typeof EnrollmentDetail>;
export type EnrollmentDetailOutput = zod.output<typeof EnrollmentDetail>;

export const EnrollmentSettingsInput = zod.object({
  "negativeGap": zod.number().nullish(),
  "topK": zod.int().nullish(),
  "byClass": zod.record(zod.string(), zod.number().nullable()).nullish(),
  "updatedAt": zod.iso.datetime({"offset":true}).nullish()
});

export type EnrollmentSettingsInput = zod.input<typeof EnrollmentSettingsInput>;
export type EnrollmentSettingsInputOutput = zod.output<typeof EnrollmentSettingsInput>;

export const EnrollmentSettingsResponse = zod.object({
  "negativeGap": zod.number(),
  "topK": zod.int(),
  "byClass": zod.record(zod.string(), zod.number()),
  "updatedAt": zod.iso.datetime({"offset":true}).optional(),
  "isDefault": zod.boolean().optional().describe('The organization has tuned nothing, and these are the shipped defaults.')
});

export type EnrollmentSettingsResponse = zod.input<typeof EnrollmentSettingsResponse>;
export type EnrollmentSettingsResponseOutput = zod.output<typeof EnrollmentSettingsResponse>;

export const HealthMessage = zod.object({
  "id": zod.string().describe('Unique per record.'),
  "seq": zod.int().optional().describe('The log\'s read cursor, set by the cloud as it stores the record.'),
  "type": zod.string(),
  "severity": zod.string(),
  "objectId": zod.string().describe('What the problem is about, as the id of a camera, a storage or a GPU; empty when it is about the server itself.'),
  "serverId": zod.string().optional().describe('The node the record is about; absent for one about no node.'),
  "text": zod.string().describe('The event in a sentence, for a reader that does not know the type.'),
  "description": zod.string().optional().describe('What the source adds for a reader who opens the message; absent when nothing.'),
  "createdAtMs": zod.int().describe('When the event happened.'),
  "details": zod.unknown().optional().describe('The type\'s own fields, as its source wrote them; absent when it has none.')
});

export type HealthMessage = zod.input<typeof HealthMessage>;
export type HealthMessageOutput = zod.output<typeof HealthMessage>;

export const HealthMessageInput = zod.object({
  "id": zod.string().nullish(),
  "seq": zod.int().nullish(),
  "type": zod.string().nullish(),
  "severity": zod.string().nullish(),
  "objectId": zod.string().nullish(),
  "serverId": zod.string().nullish(),
  "text": zod.string().nullish(),
  "description": zod.string().nullish(),
  "createdAtMs": zod.int().nullish(),
  "details": zod.unknown().optional()
});

export type HealthMessageInput = zod.input<typeof HealthMessageInput>;
export type HealthMessageInputOutput = zod.output<typeof HealthMessageInput>;

export const HelloRequest = zod.object({
  "serverId": zod.string().nullish(),
  "serverName": zod.string().nullish(),
  "publicKey": zod.string().nullish(),
  "kind": zod.string().nullish()
});

export type HelloRequest = zod.input<typeof HelloRequest>;
export type HelloRequestOutput = zod.output<typeof HelloRequest>;

export const InternalMembership = zod.object({
  "orgId": zod.string(),
  "role": zod.string(),
  "status": zod.string()
});

export type InternalMembership = zod.input<typeof InternalMembership>;
export type InternalMembershipOutput = zod.output<typeof InternalMembership>;

export const InternalServerResponse = zod.object({
  "organizationId": zod.string(),
  "serverId": zod.string(),
  "publicKeyPem": zod.string()
});

export type InternalServerResponse = zod.input<typeof InternalServerResponse>;
export type InternalServerResponseOutput = zod.output<typeof InternalServerResponse>;

export const InternalUserResponse = zod.object({
  "email": zod.string(),
  "organizationId": zod.string(),
  "memberships": zod.array(InternalMembership).nullable()
});

export type InternalUserResponse = zod.input<typeof InternalUserResponse>;
export type InternalUserResponseOutput = zod.output<typeof InternalUserResponse>;

export const InviteActionRequest = zod.object({
  "orgId": zod.string().nullish()
});

export type InviteActionRequest = zod.input<typeof InviteActionRequest>;
export type InviteActionRequestOutput = zod.output<typeof InviteActionRequest>;

export const InviteRequest = zod.object({
  "email": zod.string().nullish()
});

export type InviteRequest = zod.input<typeof InviteRequest>;
export type InviteRequestOutput = zod.output<typeof InviteRequest>;

export const Jwk = zod.object({
  "kty": zod.string(),
  "crv": zod.string().optional(),
  "alg": zod.string(),
  "use": zod.string(),
  "kid": zod.string(),
  "x": zod.string().optional(),
  "n": zod.string().optional(),
  "e": zod.string().optional()
});

export type Jwk = zod.input<typeof Jwk>;
export type JwkOutput = zod.output<typeof Jwk>;

export const Jwks = zod.object({
  "keys": zod.array(Jwk).nullable()
});

export type Jwks = zod.input<typeof Jwks>;
export type JwksOutput = zod.output<typeof Jwks>;

export const LatestBuild = zod.object({
  "version": zod.string(),
  "publishedAt": zod.iso.datetime({"offset":true}),
  "artifacts": zod.array(BuildArtifact)
}).describe('A registry row as a visitor sees it, with nothing about who published it.');

export type LatestBuild = zod.input<typeof LatestBuild>;
export type LatestBuildOutput = zod.output<typeof LatestBuild>;

export const LatestBuildResponse = zod.object({
  "build": zod.union([LatestBuild,zod.null()])
});

export type LatestBuildResponse = zod.input<typeof LatestBuildResponse>;
export type LatestBuildResponseOutput = zod.output<typeof LatestBuildResponse>;

export const Layout = zod.object({
  "id": zod.string(),
  "name": zod.string(),
  "cellAspect": zod.enum(['auto', '16:9', '4:3', '1:1']),
  "gap": zod.enum(['zero', 'small', 'medium', 'large', 'xlarge']),
  "items": zod.unknown(),
  "isVideoWall": zod.boolean(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "updatedAt": zod.iso.datetime({"offset":true})
});

export type Layout = zod.input<typeof Layout>;
export type LayoutOutput = zod.output<typeof Layout>;

export const LayoutInput = zod.object({
  "id": zod.string().nullish(),
  "name": zod.string().nullish(),
  "cellAspect": zod.string().nullish(),
  "gap": zod.string().nullish(),
  "items": zod.unknown().optional(),
  "isVideoWall": zod.boolean().nullish(),
  "createdAt": zod.iso.datetime({"offset":true}).nullish(),
  "updatedAt": zod.iso.datetime({"offset":true}).nullish()
});

export type LayoutInput = zod.input<typeof LayoutInput>;
export type LayoutInputOutput = zod.output<typeof LayoutInput>;

export const LocalLoginResponse = zod.object({
  "token": zod.string()
});

export type LocalLoginResponse = zod.input<typeof LocalLoginResponse>;
export type LocalLoginResponseOutput = zod.output<typeof LocalLoginResponse>;

export const Location = zod.object({
  "id": zod.string(),
  "name": zod.string(),
  "createdAt": zod.iso.datetime({"offset":true})
});

export type Location = zod.input<typeof Location>;
export type LocationOutput = zod.output<typeof Location>;

export const LocationRequest = zod.object({
  "name": zod.string().nullish()
});

export type LocationRequest = zod.input<typeof LocationRequest>;
export type LocationRequestOutput = zod.output<typeof LocationRequest>;

export const LocationUpdateSummary = zod.object({
  "locationId": zod.string().nullable(),
  "servers": zod.int(),
  "versions": zod.record(zod.string(), zod.int()).nullable(),
  "states": zod.record(zod.string(), zod.int()).nullable(),
  "allDownloaded": zod.boolean(),
  "unhealthy": zod.record(zod.string(), zod.array(zod.string()).nullable()).nullable()
});

export type LocationUpdateSummary = zod.input<typeof LocationUpdateSummary>;
export type LocationUpdateSummaryOutput = zod.output<typeof LocationUpdateSummary>;

export const LocationWithServers = zod.object({
  "id": zod.string(),
  "name": zod.string(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "serverIds": zod.array(zod.string())
});

export type LocationWithServers = zod.input<typeof LocationWithServers>;
export type LocationWithServersOutput = zod.output<typeof LocationWithServers>;

export const ReleaseManifest = zod.object({
  "version": zod.string(),
  "changeSet": zod.string(),
  "publishedAt": zod.iso.datetime({"offset":true}),
  "artifacts": zod.array(BuildArtifact)
});

export type ReleaseManifest = zod.input<typeof ReleaseManifest>;
export type ReleaseManifestOutput = zod.output<typeof ReleaseManifest>;

export const ManifestStatus = zod.object({
  "manifest": zod.union([ReleaseManifest,zod.null()]),
  "error": zod.string().optional()
});

export type ManifestStatus = zod.input<typeof ManifestStatus>;
export type ManifestStatusOutput = zod.output<typeof ManifestStatus>;

export const OrgRef = zod.object({
  "orgId": zod.string(),
  "orgName": zod.string(),
  "role": zod.enum(['admin', 'viewer'])
});

export type OrgRef = zod.input<typeof OrgRef>;
export type OrgRefOutput = zod.output<typeof OrgRef>;

export const PendingInvite = zod.object({
  "orgId": zod.string(),
  "orgName": zod.string(),
  "invitedBy": zod.string(),
  "invitedByName": zod.string().optional(),
  "invitedByPicture": zod.string().optional()
});

export type PendingInvite = zod.input<typeof PendingInvite>;
export type PendingInviteOutput = zod.output<typeof PendingInvite>;

export const MeResponse = zod.object({
  "email": zod.string(),
  "enrolled": zod.boolean(),
  "orgs": zod.array(OrgRef).nullable(),
  "orgId": zod.string().optional(),
  "orgName": zod.string().optional(),
  "pendingInvites": zod.array(PendingInvite).optional(),
  "token": zod.string().optional().describe('The session token minted again after a call that adds a membership.'),
  "platformAdmin": zod.boolean().optional().describe('The caller may administer the deployment itself, as its build registry.')
});

export type MeResponse = zod.input<typeof MeResponse>;
export type MeResponseOutput = zod.output<typeof MeResponse>;

export const MemberPermissions = zod.object({
  "email": zod.string(),
  "role": zod.enum(['admin', 'viewer']),
  "scopeId": zod.string().nullable(),
  "schedule": zod.string().nullable().describe('The weekly mask of 21 bytes, in base64.')
});

export type MemberPermissions = zod.input<typeof MemberPermissions>;
export type MemberPermissionsOutput = zod.output<typeof MemberPermissions>;

export const MemberPermissionsInput = zod.object({
  "email": zod.string().nullish(),
  "role": zod.string().nullish(),
  "scopeId": zod.string().nullish(),
  "schedule": zod.string().nullish()
});

export type MemberPermissionsInput = zod.input<typeof MemberPermissionsInput>;
export type MemberPermissionsInputOutput = zod.output<typeof MemberPermissionsInput>;

export const Organization = zod.object({
  "id": zod.string(),
  "name": zod.string(),
  "channelPartnerId": zod.string().optional()
});

export type Organization = zod.input<typeof Organization>;
export type OrganizationOutput = zod.output<typeof Organization>;

export const OrganizationInput = zod.object({
  "name": zod.string().nullish(),
  "channelPartnerId": zod.string().nullish()
});

export type OrganizationInput = zod.input<typeof OrganizationInput>;
export type OrganizationInputOutput = zod.output<typeof OrganizationInput>;

export const PendingResponse = zod.object({
  "serverName": zod.string(),
  "kind": zod.enum(['media_server', 'ai_agent_toolbox']).optional(),
  "expiresAt": zod.iso.datetime({"offset":true})
});

export type PendingResponse = zod.input<typeof PendingResponse>;
export type PendingResponseOutput = zod.output<typeof PendingResponse>;

export const PublishRequest = zod.object({
  "version": zod.string().nullish()
});

export type PublishRequest = zod.input<typeof PublishRequest>;
export type PublishRequestOutput = zod.output<typeof PublishRequest>;

export const RefreshResponse = zod.object({
  "token": zod.string(),
  "expiresAt": zod.int()
});

export type RefreshResponse = zod.input<typeof RefreshResponse>;
export type RefreshResponseOutput = zod.output<typeof RefreshResponse>;

export const ReplaceServerRequest = zod.object({
  "targetServerId": zod.string().nullish()
});

export type ReplaceServerRequest = zod.input<typeof ReplaceServerRequest>;
export type ReplaceServerRequestOutput = zod.output<typeof ReplaceServerRequest>;

export const RetainedVersionsResponse = zod.object({
  "versions": zod.array(zod.string()).nullable()
});

export type RetainedVersionsResponse = zod.input<typeof RetainedVersionsResponse>;
export type RetainedVersionsResponseOutput = zod.output<typeof RetainedVersionsResponse>;

export const RevokeSessionsResponse = zod.object({
  "revoked": zod.int()
});

export type RevokeSessionsResponse = zod.input<typeof RevokeSessionsResponse>;
export type RevokeSessionsResponseOutput = zod.output<typeof RevokeSessionsResponse>;




export const Rule = zod.object({
  "id": zod.string(),
  "ruleSetId": zod.string(),
  "name": zod.string(),
  "enabled": zod.boolean(),
  "eventLogic": zod.enum(['logicAnd', 'logicOr']),
  "eventLogicTimeoutS": zod.int().optional(),
  "events": zod.unknown(),
  "actions": zod.unknown(),
  "conditions": zod.unknown().optional(),
  "comment": zod.string().optional(),
  "schedule": zod.string().optional().describe('The weekly hours in which the rule works, as a mask. Absent, all hours.'),
  "triggerLimit": zod.int().min(1).optional().describe('How many times the rule triggers before it disables itself. Absent, it never stops.'),
  "expiresAt": zod.string().optional().describe('The time at which the rule disables itself. Absent, it does not expire.'),
  "description": zod.string().optional()
});

export type Rule = zod.input<typeof Rule>;
export type RuleOutput = zod.output<typeof Rule>;

export const RuleInput = zod.object({
  "id": zod.string().nullish(),
  "ruleSetId": zod.string().nullish(),
  "name": zod.string().nullish(),
  "enabled": zod.boolean().nullish(),
  "eventLogic": zod.string().nullish(),
  "eventLogicTimeoutS": zod.int().nullish(),
  "events": zod.unknown().optional(),
  "actions": zod.unknown().optional(),
  "conditions": zod.unknown().optional(),
  "comment": zod.string().nullish(),
  "schedule": zod.string().nullish(),
  "triggerLimit": zod.int().nullish(),
  "expiresAt": zod.string().nullish(),
  "description": zod.string().nullish()
});

export type RuleInput = zod.input<typeof RuleInput>;
export type RuleInputOutput = zod.output<typeof RuleInput>;

export const RuleSet = zod.object({
  "version": zod.int(),
  "id": zod.string(),
  "name": zod.string(),
  "enabled": zod.boolean()
});

export type RuleSet = zod.input<typeof RuleSet>;
export type RuleSetOutput = zod.output<typeof RuleSet>;

export const RuleSetInput = zod.object({
  "version": zod.int().nullish(),
  "id": zod.string().nullish(),
  "name": zod.string().nullish(),
  "enabled": zod.boolean().nullish()
});

export type RuleSetInput = zod.input<typeof RuleSetInput>;
export type RuleSetInputOutput = zod.output<typeof RuleSetInput>;

export const Server = zod.object({
  "id": zod.string(),
  "name": zod.string(),
  "kind": zod.enum(['media_server', 'ai_agent_toolbox']).optional(),
  "sslCertificate": zod.string().optional(),
  "params": zod.record(zod.string(), zod.string()).optional(),
  "locationId": zod.string().nullable(),
  "timeZone": zod.string().optional().describe('The IANA zone id that the media_server reports.')
});

export type Server = zod.input<typeof Server>;
export type ServerOutput = zod.output<typeof Server>;

export const ServerInput = zod.object({
  "id": zod.string().nullish(),
  "name": zod.string().nullish(),
  "kind": zod.string().nullish(),
  "sslCertificate": zod.string().nullish(),
  "params": zod.record(zod.string(), zod.string().nullable()).nullish(),
  "locationId": zod.string().nullish(),
  "timeZone": zod.string().nullish()
});

export type ServerInput = zod.input<typeof ServerInput>;
export type ServerInputOutput = zod.output<typeof ServerInput>;

export const ServerReplacement = zod.object({
  "devices": zod.int(),
  "storages": zod.int()
});

export type ServerReplacement = zod.input<typeof ServerReplacement>;
export type ServerReplacementOutput = zod.output<typeof ServerReplacement>;

export const ServerUpdateStatus = zod.object({
  "serverId": zod.string(),
  "version": zod.string(),
  "changeSet": zod.string(),
  "platform": zod.string(),
  "phase": zod.string(),
  "targetVersion": zod.string().optional(),
  "downloadAt": zod.iso.datetime({"offset":true}).optional(),
  "downloadedVersion": zod.string().optional(),
  "applyStartedAt": zod.iso.datetime({"offset":true}).optional(),
  "lastOutcome": zod.string(),
  "lastReason": zod.string().optional(),
  "lastExitCode": zod.int().optional(),
  "lastLogPath": zod.string().optional(),
  "failureVersion": zod.string().optional(),
  "failureCount": zod.int(),
  "reportedAt": zod.iso.datetime({"offset":true}).describe('When the NATS server stored the report.'),
  "locationId": zod.string().nullable(),
  "state": zod.enum(['upToDate', 'behind', 'planned', 'downloaded', 'updating', 'stalled', 'failed', 'unknown'])
});

export type ServerUpdateStatus = zod.input<typeof ServerUpdateStatus>;
export type ServerUpdateStatusOutput = zod.output<typeof ServerUpdateStatus>;

export const SessionRequest = zod.object({
  "token": zod.string().nullish(),
  "orgId": zod.string().nullish()
});

export type SessionRequest = zod.input<typeof SessionRequest>;
export type SessionRequestOutput = zod.output<typeof SessionRequest>;

export const SetHiddenRequest = zod.object({
  "hidden": zod.boolean().nullish()
});

export type SetHiddenRequest = zod.input<typeof SetHiddenRequest>;
export type SetHiddenRequestOutput = zod.output<typeof SetHiddenRequest>;

export const SetKeyRequest = zod.object({
  "password": zod.string().nullish()
});

export type SetKeyRequest = zod.input<typeof SetKeyRequest>;
export type SetKeyRequestOutput = zod.output<typeof SetKeyRequest>;

export const SetServersRequest = zod.object({
  "serverIds": zod.array(zod.string().nullable()).nullish()
});

export type SetServersRequest = zod.input<typeof SetServersRequest>;
export type SetServersRequestOutput = zod.output<typeof SetServersRequest>;

export const SetupRequest = zod.object({
  "orgName": zod.string().nullish()
});

export type SetupRequest = zod.input<typeof SetupRequest>;
export type SetupRequestOutput = zod.output<typeof SetupRequest>;

export const Storage = zod.object({
  "id": zod.string(),
  "serverId": zod.string(),
  "url": zod.string(),
  "secret": zod.string().optional(),
  "autoDiscovered": zod.boolean().describe('A disk the server found itself, which stays a storage.'),
  "reservedSpaceGB": zod.int().describe('Free space the archive cleaner keeps here.'),
  "usedForWriting": zod.boolean()
});

export type Storage = zod.input<typeof Storage>;
export type StorageOutput = zod.output<typeof Storage>;

export const StorageInput = zod.object({
  "id": zod.string().nullish(),
  "serverId": zod.string().nullish(),
  "url": zod.string().nullish(),
  "secret": zod.string().nullish(),
  "autoDiscovered": zod.boolean().nullish(),
  "reservedSpaceGB": zod.int().nullish(),
  "usedForWriting": zod.boolean().nullish()
});

export type StorageInput = zod.input<typeof StorageInput>;
export type StorageInputOutput = zod.output<typeof StorageInput>;

export const TelegramChatInput = zod.object({
  "id": zod.int().nullish(),
  "type": zod.string().nullish()
});

export type TelegramChatInput = zod.input<typeof TelegramChatInput>;
export type TelegramChatInputOutput = zod.output<typeof TelegramChatInput>;

export const TelegramMessageInput = zod.object({
  "message_id": zod.int().nullish(),
  "text": zod.string().nullish(),
  "chat": zod.union([TelegramChatInput,zod.null()]).optional()
});

export type TelegramMessageInput = zod.input<typeof TelegramMessageInput>;
export type TelegramMessageInputOutput = zod.output<typeof TelegramMessageInput>;

export const TelegramCallbackQueryInput = zod.object({
  "id": zod.string().nullish(),
  "data": zod.string().nullish(),
  "message": zod.union([TelegramMessageInput,zod.null()]).optional()
});

export type TelegramCallbackQueryInput = zod.input<typeof TelegramCallbackQueryInput>;
export type TelegramCallbackQueryInputOutput = zod.output<typeof TelegramCallbackQueryInput>;

export const TelegramLink = zod.object({
  "deepLink": zod.string(),
  "expiresAt": zod.iso.datetime({"offset":true})
});

export type TelegramLink = zod.input<typeof TelegramLink>;
export type TelegramLinkOutput = zod.output<typeof TelegramLink>;

export const TelegramStatus = zod.object({
  "linked": zod.boolean(),
  "chatId": zod.int().optional()
});

export type TelegramStatus = zod.input<typeof TelegramStatus>;
export type TelegramStatusOutput = zod.output<typeof TelegramStatus>;

export const TelegramUpdateInput = zod.object({
  "update_id": zod.int().nullish(),
  "message": zod.union([TelegramMessageInput,zod.null()]).optional(),
  "callback_query": zod.union([TelegramCallbackQueryInput,zod.null()]).optional()
});

export type TelegramUpdateInput = zod.input<typeof TelegramUpdateInput>;
export type TelegramUpdateInputOutput = zod.output<typeof TelegramUpdateInput>;

export const TurnCredentials = zod.object({
  "username": zod.string().describe('It holds a colon (RFC 7635), so it rides its own field and never goes into a URL.'),
  "password": zod.string(),
  "ttl": zod.int(),
  "urls": zod.array(zod.string())
});

export type TurnCredentials = zod.input<typeof TurnCredentials>;
export type TurnCredentialsOutput = zod.output<typeof TurnCredentials>;

export const UnlockRequest = zod.object({
  "password": zod.string().nullish(),
  "keyId": zod.string().nullish(),
  "salt": zod.string().nullish()
});

export type UnlockRequest = zod.input<typeof UnlockRequest>;
export type UnlockRequestOutput = zod.output<typeof UnlockRequest>;

export const User = zod.object({
  "email": zod.string(),
  "organizationId": zod.string(),
  "createdAt": zod.iso.datetime({"offset":true}),
  "displayName": zod.string().optional(),
  "pictureUrl": zod.string().optional(),
  "role": zod.enum(['admin', 'viewer']),
  "status": zod.enum(['active', 'invited']),
  "invitedBy": zod.string().optional(),
  "hidden": zod.boolean()
});

export type User = zod.input<typeof User>;
export type UserOutput = zod.output<typeof User>;
/**
 * @summary Get user by email
 */
export const InternalGetUserByEmailQueryParams = zod.object({
  "email": zod.string()
})

export const InternalGetUserByEmailResponse = InternalUserResponse


/**
 * @summary Get server
 */
export const InternalGetServerParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string()
})

export const InternalGetServerResponse = InternalServerResponse


/**
 * The resources of one kind that a member can reach, for the services that check permissions but do not store them.
 * @summary List accessible
 */
export const InternalListAccessibleQueryParams = zod.object({
  "orgId": zod.string(),
  "email": zod.string(),
  "kind": zod.string()
})

export const InternalListAccessibleResponse = AccessibleResponse


/**
 * Whether a member may take an action on a resource, for the services that check permissions but do not store them.
 * @summary Check permission
 */
export const InternalCheckPermissionQueryParams = zod.object({
  "orgId": zod.string(),
  "email": zod.string(),
  "action": zod.string(),
  "kind": zod.string().optional(),
  "id": zod.string().optional(),
  "serverId": zod.string().optional()
})

export const InternalCheckPermissionResponse = CheckResponse


/**
 * @summary Get client config
 */
export const GetClientConfigResponse = ClientConfig


/**
 * @summary Create session
 */
export const CreateSessionBody = SessionRequest

export const CreateSessionResponse = zod.object({
  "status": zod.string(),
  "token": zod.string()
})


/**
 * A new session token for the refresh cookie. The request needs no session token, because the session token may have expired.
 * @summary Refresh session
 */
export const RefreshSessionResponse = RefreshResponse


/**
 * The sign-in of Google's redirect mode. Google posts the credential to this path as a form, before the browser holds a session cookie, so no session middleware guards the path.
 * @summary Google redirect
 */
export const GoogleRedirectBody = zod.object({
  "credential": zod.string().optional(),
  "g_csrf_token": zod.string().optional()
})

export const GoogleRedirectResponse = zod.void()


/**
 * @summary Logout
 */
export const LogoutResponse = zod.object({
  "status": zod.string()
})


/**
 * @summary Get session JWKS
 */
export const GetSessionJwksResponse = Jwks


/**
 * @summary Get session JWKS
 */
export const InternalGetSessionJwksResponse = Jwks


/**
 * @summary Get node JWKS
 */
export const GetNodeJwksQueryParams = zod.object({
  "orgId": zod.string(),
  "kid": zod.array(zod.string()).optional().describe('A server id. Repeated, the set holds only the named nodes, the form that a verifying node uses. Without it, every node of the organization.')
})

export const GetNodeJwksResponse = Jwks


/**
 * @summary Create local login token
 */
export const CreateLocalLoginTokenResponse = LocalLoginResponse


/**
 * @summary Revoke all sessions
 */
export const RevokeAllSessionsResponse = zod.object({
  "status": zod.string()
})


/**
 * @summary Get me
 */
export const GetMeResponse = MeResponse


/**
 * @summary Setup
 */
export const SetupBody = SetupRequest

export const SetupResponse = MeResponse


/**
 * The invited user accepts the invitation and becomes a member. The user is not a member yet, so the request names no organization.
 * @summary Accept invite
 */
export const AcceptInviteBody = InviteActionRequest

export const AcceptInviteResponse = MeResponse


/**
 * The invited user declines the invitation. The user is not a member, so the request names no organization.
 * @summary Decline invite
 */
export const DeclineInviteBody = InviteActionRequest

export const DeclineInviteResponse = MeResponse


/**
 * @summary Provisioning hello
 */
export const ProvisioningHelloBody = HelloRequest

export const ProvisioningHelloResponse = zod.unknown()


/**
 * The name of the server that waits under a provisioning code. That server belongs to no organization yet, so the caller only needs to be signed in.
 * @summary Get pending server
 */
export const GetPendingServerParams = zod.object({
  "code": zod.string()
})

export const GetPendingServerResponse = PendingResponse


/**
 * @summary Claim server
 */
export const ClaimServerQueryParams = zod.object({
  "orgId": zod.string()
})

export const ClaimServerBody = ClaimRequest

export const ClaimServerResponse = ClaimResponse


/**
 * @summary List channel partners
 */
export const ListChannelPartnersResponseItem = ChannelPartner
export const ListChannelPartnersResponse = zod.array(ListChannelPartnersResponseItem)


/**
 * @summary Create channel partner
 */
export const CreateChannelPartnerBody = ChannelPartnerInput

export const CreateChannelPartnerResponse = ChannelPartner


/**
 * @summary Get channel partner
 */
export const GetChannelPartnerParams = zod.object({
  "id": zod.string()
})

export const GetChannelPartnerResponse = ChannelPartner


/**
 * @summary Update channel partner
 */
export const UpdateChannelPartnerParams = zod.object({
  "id": zod.string()
})

export const UpdateChannelPartnerBody = ChannelPartnerInput

export const UpdateChannelPartnerResponse = ChannelPartner


/**
 * @summary Delete channel partner
 */
export const DeleteChannelPartnerParams = zod.object({
  "id": zod.string()
})

export const DeleteChannelPartnerResponse = zod.void()


/**
 * @summary List organizations
 */
export const ListOrganizationsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListOrganizationsResponseItem = Organization
export const ListOrganizationsResponse = zod.array(ListOrganizationsResponseItem)


/**
 * @summary Get organization
 */
export const GetOrganizationParams = zod.object({
  "id": zod.string()
})

export const GetOrganizationQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetOrganizationResponse = Organization


/**
 * @summary Update organization
 */
export const UpdateOrganizationParams = zod.object({
  "id": zod.string()
})

export const UpdateOrganizationQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateOrganizationBody = OrganizationInput

export const UpdateOrganizationResponse = Organization


/**
 * @summary Delete organization
 */
export const DeleteOrganizationParams = zod.object({
  "id": zod.string()
})

export const DeleteOrganizationQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteOrganizationResponse = zod.void()


/**
 * The time zone of an organization. The metadata service needs it to show times to the AI chat in that zone.
 * @summary Get organization time zone
 */
export const InternalGetOrganizationTimeZoneQueryParams = zod.object({
  "orgId": zod.string()
})

export const InternalGetOrganizationTimeZoneResponse = zod.object({
  "timeZone": zod.string()
})


/**
 * @summary List users
 */
export const ListUsersQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListUsersResponseItem = User
export const ListUsersResponse = zod.array(ListUsersResponseItem)


/**
 * @summary Invite user
 */
export const InviteUserQueryParams = zod.object({
  "orgId": zod.string()
})

export const InviteUserBody = InviteRequest

export const InviteUserResponse = User


/**
 * @summary Set user hidden
 */
export const SetUserHiddenParams = zod.object({
  "email": zod.string()
})

export const SetUserHiddenQueryParams = zod.object({
  "orgId": zod.string()
})

export const SetUserHiddenBody = SetHiddenRequest

export const SetUserHiddenResponse = zod.object({
  "email": zod.unknown(),
  "hidden": zod.unknown()
})


/**
 * @summary Delete user
 */
export const DeleteUserParams = zod.object({
  "email": zod.string()
})

export const DeleteUserQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteUserResponse = DeleteResponse


/**
 * @summary Revoke user sessions
 */
export const RevokeUserSessionsParams = zod.object({
  "email": zod.string()
})

export const RevokeUserSessionsQueryParams = zod.object({
  "orgId": zod.string()
})

export const RevokeUserSessionsResponse = RevokeSessionsResponse


/**
 * @summary List member permissions
 */
export const ListMemberPermissionsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListMemberPermissionsResponseItem = MemberPermissions
export const ListMemberPermissionsResponse = zod.array(ListMemberPermissionsResponseItem)


/**
 * @summary Get member permissions
 */
export const GetMemberPermissionsParams = zod.object({
  "email": zod.string()
})

export const GetMemberPermissionsQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetMemberPermissionsResponse = MemberPermissions


/**
 * @summary Set member permissions
 */
export const SetMemberPermissionsParams = zod.object({
  "email": zod.string()
})

export const SetMemberPermissionsQueryParams = zod.object({
  "orgId": zod.string()
})

export const SetMemberPermissionsBody = MemberPermissionsInput

export const SetMemberPermissionsResponse = MemberPermissions


/**
 * @summary List servers
 */
export const ListServersQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListServersResponseItem = Server
export const ListServersResponse = zod.array(ListServersResponseItem)


/**
 * @summary Upsert servers
 */
export const UpsertServersQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertServersBodyItem = ServerInput
export const UpsertServersBody = zod.array(UpsertServersBodyItem)

export const UpsertServersResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Get server
 */
export const GetServerParams = zod.object({
  "id": zod.string()
})

export const GetServerQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetServerResponse = Server


/**
 * @summary Update server
 */
export const UpdateServerParams = zod.object({
  "id": zod.string()
})

export const UpdateServerQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateServerBody = ServerInput

export const UpdateServerResponse = Server


/**
 * @summary Delete server
 */
export const DeleteServerParams = zod.object({
  "id": zod.string()
})

export const DeleteServerQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteServerResponse = zod.void()


/**
 * @summary Replace server
 */
export const ReplaceServerParams = zod.object({
  "id": zod.string()
})

export const ReplaceServerQueryParams = zod.object({
  "orgId": zod.string()
})

export const ReplaceServerBody = ReplaceServerRequest

export const ReplaceServerResponse = ServerReplacement


/**
 * @summary List locations
 */
export const ListLocationsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListLocationsResponseItem = LocationWithServers
export const ListLocationsResponse = zod.array(ListLocationsResponseItem)


/**
 * @summary Create location
 */
export const CreateLocationQueryParams = zod.object({
  "orgId": zod.string()
})

export const CreateLocationBody = LocationRequest

export const CreateLocationResponse = LocationWithServers


/**
 * @summary Get location
 */
export const GetLocationParams = zod.object({
  "id": zod.string()
})

export const GetLocationQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetLocationResponse = LocationWithServers


/**
 * @summary Update location
 */
export const UpdateLocationParams = zod.object({
  "id": zod.string()
})

export const UpdateLocationQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateLocationBody = LocationRequest

export const UpdateLocationResponse = Location


/**
 * @summary Delete location
 */
export const DeleteLocationParams = zod.object({
  "id": zod.string()
})

export const DeleteLocationQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteLocationResponse = zod.void()


/**
 * @summary Set location servers
 */
export const SetLocationServersParams = zod.object({
  "id": zod.string()
})

export const SetLocationServersQueryParams = zod.object({
  "orgId": zod.string()
})

export const SetLocationServersBody = SetServersRequest

export const SetLocationServersResponse = LocationWithServers


/**
 * @summary Upload binary file
 */
export const UploadBinaryFileQueryParams = zod.object({
  "orgId": zod.string()
})

export const UploadBinaryFileBody = zod.object({
  "name": zod.string().optional(),
  "file": zod.instanceof(Blob).optional()
})

export const UploadBinaryFileResponse = BinaryFile


/**
 * @summary List binary files
 */
export const ListBinaryFilesQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListBinaryFilesResponseItem = BinaryFile
export const ListBinaryFilesResponse = zod.array(ListBinaryFilesResponseItem)


/**
 * @summary Get binary file
 */
export const GetBinaryFileParams = zod.object({
  "id": zod.string()
})

export const GetBinaryFileQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetBinaryFileResponse = zod.unknown()


/**
 * @summary Delete binary file
 */
export const DeleteBinaryFileParams = zod.object({
  "id": zod.string()
})

export const DeleteBinaryFileQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteBinaryFileResponse = zod.void()


/**
 * @summary List devices
 */
export const ListDevicesQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListDevicesResponseItem = Device
export const ListDevicesResponse = zod.array(ListDevicesResponseItem)


/**
 * @summary Upsert devices
 */
export const UpsertDevicesQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertDevicesBodyItem = DeviceInput
export const UpsertDevicesBody = zod.array(UpsertDevicesBodyItem)

export const UpsertDevicesResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Get device
 */
export const getDevicePathIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const GetDeviceParams = zod.object({
  "id": zod.string().regex(getDevicePathIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const GetDeviceQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetDeviceResponse = Device


/**
 * @summary Update device
 */
export const updateDevicePathIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const UpdateDeviceParams = zod.object({
  "id": zod.string().regex(updateDevicePathIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const UpdateDeviceQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateDeviceBody = DeviceInput

export const UpdateDeviceResponse = Device


/**
 * @summary Delete device
 */
export const deleteDevicePathIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const DeleteDeviceParams = zod.object({
  "id": zod.string().regex(deleteDevicePathIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const DeleteDeviceQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteDeviceResponse = zod.void()


/**
 * @summary Create device web session
 */
export const createDeviceWebSessionPathIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const CreateDeviceWebSessionParams = zod.object({
  "id": zod.string().regex(createDeviceWebSessionPathIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const CreateDeviceWebSessionQueryParams = zod.object({
  "serverId": zod.string().describe('The server of the camera, since device ids repeat across servers.'),
  "orgId": zod.string()
})

export const CreateDeviceWebSessionResponse = zod.object({
  "url": zod.url(),
  "expiresAt": zod.iso.datetime({"offset":true})
})


/**
 * @summary List agents
 */
export const ListAgentsQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional()
})

export const ListAgentsResponseItem = Agent
export const ListAgentsResponse = zod.array(ListAgentsResponseItem)


/**
 * @summary Upsert agents
 */
export const UpsertAgentsQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertAgentsBodyItem = AgentInput
export const UpsertAgentsBody = zod.array(UpsertAgentsBodyItem)

export const UpsertAgentsResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Get agent
 */
export const GetAgentParams = zod.object({
  "agentId": zod.string()
})

export const GetAgentQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional()
})

export const GetAgentResponse = Agent


/**
 * @summary Update agent
 */
export const UpdateAgentParams = zod.object({
  "agentId": zod.string()
})

export const UpdateAgentQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional()
})

export const UpdateAgentBody = AgentInput

export const UpdateAgentResponse = Agent


/**
 * @summary Delete agent
 */
export const DeleteAgentParams = zod.object({
  "agentId": zod.string()
})

export const DeleteAgentQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional()
})

export const DeleteAgentResponse = zod.void()


/**
 * @summary Get agent settings
 */
export const GetAgentSettingsParams = zod.object({
  "agentId": zod.string()
})

export const GetAgentSettingsQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional()
})

export const GetAgentSettingsResponse = zod.record(zod.string(), zod.string()).nullable()


/**
 * @summary Replace agent settings
 */
export const ReplaceAgentSettingsParams = zod.object({
  "agentId": zod.string()
})

export const ReplaceAgentSettingsQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional()
})

export const ReplaceAgentSettingsBody = zod.record(zod.string(), zod.string().nullable()).nullable()

export const ReplaceAgentSettingsResponse = zod.record(zod.string(), zod.string()).nullable()


/**
 * @summary Patch agent settings
 */
export const PatchAgentSettingsParams = zod.object({
  "agentId": zod.string()
})

export const PatchAgentSettingsQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional()
})

export const PatchAgentSettingsBody = zod.record(zod.string(), zod.string().nullable()).nullable()

export const PatchAgentSettingsResponse = zod.record(zod.string(), zod.string()).nullable()


/**
 * The agents of an organization. The metadata service reads their manifests, which declare which custom attributes are numeric.
 * @summary List agents
 */
export const InternalListAgentsQueryParams = zod.object({
  "orgId": zod.string().optional(),
  "serverId": zod.string().optional()
})

export const InternalListAgentsResponseItem = Agent
export const InternalListAgentsResponse = zod.array(InternalListAgentsResponseItem)


/**
 * @summary Upsert device agents
 */
export const UpsertDeviceAgentsQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertDeviceAgentsBodyItem = DeviceAgentInput
export const UpsertDeviceAgentsBody = zod.array(UpsertDeviceAgentsBodyItem)

export const UpsertDeviceAgentsResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Delete device agent by composite id
 */
export const DeleteDeviceAgentByCompositeIdParams = zod.object({
  "id": zod.string()
})

export const DeleteDeviceAgentByCompositeIdQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteDeviceAgentByCompositeIdResponse = zod.void()


/**
 * @summary Upsert agent devices
 */
export const UpsertAgentDevicesParams = zod.object({
  "agentId": zod.string()
})

export const UpsertAgentDevicesQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertAgentDevicesBodyItem = DeviceAgentInput
export const UpsertAgentDevicesBody = zod.array(UpsertAgentDevicesBodyItem)

export const UpsertAgentDevicesResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Get agent device
 */
export const getAgentDevicePathDeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const GetAgentDeviceParams = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string().regex(getAgentDevicePathDeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const GetAgentDeviceQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetAgentDeviceResponse = DeviceAgent


/**
 * @summary Update agent device
 */
export const updateAgentDevicePathDeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const UpdateAgentDeviceParams = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string().regex(updateAgentDevicePathDeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const UpdateAgentDeviceQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateAgentDeviceBody = DeviceAgentInput

export const UpdateAgentDeviceResponse = DeviceAgent


/**
 * @summary Delete agent device
 */
export const deleteAgentDevicePathDeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const DeleteAgentDeviceParams = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string().regex(deleteAgentDevicePathDeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const DeleteAgentDeviceQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteAgentDeviceResponse = zod.void()


/**
 * @summary Get agent device manifest
 */
export const getAgentDeviceManifestPathDeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const GetAgentDeviceManifestParams = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string().regex(getAgentDeviceManifestPathDeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const GetAgentDeviceManifestQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetAgentDeviceManifestResponse = zod.unknown()


/**
 * @summary Get agent device settings
 */
export const getAgentDeviceSettingsPathDeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const GetAgentDeviceSettingsParams = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string().regex(getAgentDeviceSettingsPathDeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const GetAgentDeviceSettingsQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetAgentDeviceSettingsResponse = zod.unknown()


/**
 * @summary Replace agent device settings
 */
export const replaceAgentDeviceSettingsPathDeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const ReplaceAgentDeviceSettingsParams = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string().regex(replaceAgentDeviceSettingsPathDeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const ReplaceAgentDeviceSettingsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ReplaceAgentDeviceSettingsBody = zod.unknown()

export const ReplaceAgentDeviceSettingsResponse = zod.unknown()


/**
 * @summary Patch agent device settings
 */
export const patchAgentDeviceSettingsPathDeviceIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const PatchAgentDeviceSettingsParams = zod.object({
  "agentId": zod.string(),
  "deviceId": zod.string().regex(patchAgentDeviceSettingsPathDeviceIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const PatchAgentDeviceSettingsQueryParams = zod.object({
  "orgId": zod.string()
})

export const PatchAgentDeviceSettingsBody = zod.record(zod.string(), zod.unknown()).nullable()

export const PatchAgentDeviceSettingsResponse = zod.unknown()


/**
 * @summary List storages
 */
export const ListStoragesQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string().optional().describe('The server whose storages to return. Without it, every storage of the organization.')
})

export const ListStoragesResponseItem = Storage
export const ListStoragesResponse = zod.array(ListStoragesResponseItem)


/**
 * @summary Upsert storages
 */
export const UpsertStoragesQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertStoragesBodyItem = StorageInput
export const UpsertStoragesBody = zod.array(UpsertStoragesBodyItem)

export const UpsertStoragesResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Delete storage
 */
export const DeleteStorageParams = zod.object({
  "id": zod.string()
})

export const DeleteStorageQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteStorageResponse = zod.void()


/**
 * @summary List bookmarks
 */



export const ListBookmarksQueryParams = zod.object({
  "orgId": zod.string(),
  "text": zod.string().optional().describe('A substring of the description, in any letter case.'),
  "limit": zod.int().min(1).optional().describe('The most bookmarks to return; the database clamps it to its own bounds.'),
  "deviceId": zod.array(DeviceId).optional().describe('Cameras, as a comma separated list, the parameter repeated, or both. A bookmark of any of them matches.'),
  "fromMs": zod.int().optional().describe('With toMs, the range [fromMs, toMs) that a bookmark must overlap, in milliseconds since the epoch.'),
  "toMs": zod.int().optional().describe('The end of the range that fromMs starts, in milliseconds since the epoch.'),
  "tag": zod.array(zod.string()).optional().describe('A tag that the bookmark must carry. Repeated, the bookmark must carry every one.')
})

export const ListBookmarksResponseItem = Bookmark
export const ListBookmarksResponse = zod.array(ListBookmarksResponseItem)


/**
 * @summary Upsert bookmarks
 */
export const UpsertBookmarksQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertBookmarksBodyItem = BookmarkInput
export const UpsertBookmarksBody = zod.array(UpsertBookmarksBodyItem)

export const UpsertBookmarksResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Get bookmark
 */
export const GetBookmarkParams = zod.object({
  "id": zod.string()
})

export const GetBookmarkQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetBookmarkResponse = Bookmark


/**
 * @summary Update bookmark
 */
export const UpdateBookmarkParams = zod.object({
  "id": zod.string()
})

export const UpdateBookmarkQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateBookmarkBody = BookmarkInput

export const UpdateBookmarkResponse = Bookmark


/**
 * @summary Delete bookmark
 */
export const DeleteBookmarkParams = zod.object({
  "id": zod.string()
})

export const DeleteBookmarkQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteBookmarkResponse = zod.void()


/**
 * @summary List bookmark tags
 */
export const listBookmarkTagsQueryLimitMax = 100;



export const ListBookmarkTagsQueryParams = zod.object({
  "limit": zod.int().min(1).max(listBookmarkTagsQueryLimitMax).optional(),
  "orgId": zod.string()
})

export const ListBookmarkTagsResponseItem = BookmarkTag
export const ListBookmarkTagsResponse = zod.array(ListBookmarkTagsResponseItem)


/**
 * @summary List rule sets
 */
export const ListRuleSetsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListRuleSetsResponseItem = RuleSet
export const ListRuleSetsResponse = zod.array(ListRuleSetsResponseItem)


/**
 * @summary Upsert rule sets
 */
export const UpsertRuleSetsQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertRuleSetsBodyItem = RuleSetInput
export const UpsertRuleSetsBody = zod.array(UpsertRuleSetsBodyItem)

export const UpsertRuleSetsResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Get rule set
 */
export const GetRuleSetParams = zod.object({
  "id": zod.string()
})

export const GetRuleSetQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetRuleSetResponse = RuleSet


/**
 * @summary Update rule set
 */
export const UpdateRuleSetParams = zod.object({
  "id": zod.string()
})

export const UpdateRuleSetQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateRuleSetBody = RuleSetInput

export const UpdateRuleSetResponse = RuleSet


/**
 * @summary Delete rule set
 */
export const DeleteRuleSetParams = zod.object({
  "id": zod.string()
})

export const DeleteRuleSetQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteRuleSetResponse = zod.void()


/**
 * @summary Get rule schema
 */
export const GetRuleSchemaQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetRuleSchemaResponse = zod.object({
  "eventTypes": zod.array(zod.unknown()),
  "actionTypes": zod.array(zod.unknown())
}).describe('The JSON schema of a rule.')


/**
 * @summary Get rule trigger count
 */
export const GetRuleTriggerCountParams = zod.object({
  "id": zod.string()
})

export const GetRuleTriggerCountQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetRuleTriggerCountResponse = zod.object({
  "count": zod.int()
})


/**
 * @summary Reset rule trigger count
 */
export const ResetRuleTriggerCountParams = zod.object({
  "id": zod.string()
})

export const ResetRuleTriggerCountQueryParams = zod.object({
  "orgId": zod.string()
})

export const ResetRuleTriggerCountResponse = zod.void()


/**
 * @summary List rules
 */
export const ListRulesQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListRulesResponseItem = Rule
export const ListRulesResponse = zod.array(ListRulesResponseItem)


/**
 * @summary Upsert rules
 */
export const UpsertRulesQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpsertRulesBodyItem = RuleInput
export const UpsertRulesBody = zod.array(UpsertRulesBodyItem)

export const UpsertRulesResponse = zod.object({
  "accepted": zod.int()
})


/**
 * @summary Get rule
 */
export const GetRuleParams = zod.object({
  "id": zod.string()
})

export const GetRuleQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetRuleResponse = Rule


/**
 * @summary Update rule
 */
export const UpdateRuleParams = zod.object({
  "id": zod.string()
})

export const UpdateRuleQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateRuleBody = RuleInput

export const UpdateRuleResponse = Rule


/**
 * @summary Delete rule
 */
export const DeleteRuleParams = zod.object({
  "id": zod.string()
})

export const DeleteRuleQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteRuleResponse = zod.void()


/**
 * @summary Get enrollment settings
 */
export const GetEnrollmentSettingsQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetEnrollmentSettingsResponse = EnrollmentSettingsResponse


/**
 * @summary Set enrollment settings
 */
export const SetEnrollmentSettingsQueryParams = zod.object({
  "orgId": zod.string()
})

export const SetEnrollmentSettingsBody = EnrollmentSettingsInput

export const SetEnrollmentSettingsResponse = EnrollmentSettingsResponse


/**
 * @summary Reset enrollment settings
 */
export const ResetEnrollmentSettingsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ResetEnrollmentSettingsResponse = EnrollmentSettingsResponse


/**
 * The object categories that privacy blur hides on this camera. The metadata service stores the best-shot images of events, and the blur setting of each camera is stored here, so the metadata service reads it before it returns a best-shot image to a member who is not an administrator.
 * @summary Get device blur categories
 */
export const internalGetDeviceBlurCategoriesPathIdRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const InternalGetDeviceBlurCategoriesParams = zod.object({
  "id": zod.string().regex(internalGetDeviceBlurCategoriesPathIdRegExp).describe('A camera\'s id, 8-4-4-4-12 hex digits with no RFC 9562 version digit.')
})

export const InternalGetDeviceBlurCategoriesQueryParams = zod.object({
  "orgId": zod.string().optional()
})

export const InternalGetDeviceBlurCategoriesResponse = zod.object({
  "blurCategories": zod.array(zod.string())
})


/**
 * @summary List enrollments
 */
export const ListEnrollmentsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListEnrollmentsResponseItem = Enrollment
export const ListEnrollmentsResponse = zod.array(ListEnrollmentsResponseItem)


/**
 * @summary Create enrollment
 */
export const CreateEnrollmentQueryParams = zod.object({
  "orgId": zod.string()
})

export const CreateEnrollmentBody = EnrollRequest

export const CreateEnrollmentResponse = EnrollResponse


/**
 * @summary Get enrollment
 */
export const GetEnrollmentParams = zod.object({
  "id": zod.string()
})

export const GetEnrollmentQueryParams = zod.object({
  "orgId": zod.string(),
  "withEmbeddings": zod.boolean().optional()
})

export const GetEnrollmentResponse = EnrollmentDetail


/**
 * @summary Update enrollment
 */
export const UpdateEnrollmentParams = zod.object({
  "id": zod.string()
})

export const UpdateEnrollmentQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateEnrollmentBody = zod.object({
  "name": zod.string().nullish(),
  "color": zod.string().nullish()
})

export const UpdateEnrollmentResponse = Enrollment


/**
 * @summary Delete enrollment
 */
export const DeleteEnrollmentParams = zod.object({
  "id": zod.string()
})

export const DeleteEnrollmentQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteEnrollmentResponse = zod.void()


/**
 * @summary Delete enrollment exemplar
 */
export const DeleteEnrollmentExemplarParams = zod.object({
  "id": zod.string(),
  "exemplarId": zod.string()
})

export const DeleteEnrollmentExemplarQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteEnrollmentExemplarResponse = zod.void()


/**
 * @summary Get enrollment exemplar image
 */
export const GetEnrollmentExemplarImageParams = zod.object({
  "id": zod.string(),
  "exemplarId": zod.string()
})

export const GetEnrollmentExemplarImageQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetEnrollmentExemplarImageResponse = zod.unknown()


/**
 * @summary Get enrollment image
 */
export const GetEnrollmentImageParams = zod.object({
  "id": zod.string()
})

export const GetEnrollmentImageQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetEnrollmentImageResponse = zod.unknown()


/**
 * The enrollments of an organization. The metadata service holds the vectors of the events, and the enrollments are stored here, so the reidentify job of the metadata service reads them here.
 * @summary List enrollments
 */
export const InternalListEnrollmentsQueryParams = zod.object({
  "orgId": zod.string().optional()
})

export const InternalListEnrollmentsResponseItem = Enrollment
export const InternalListEnrollmentsResponse = zod.array(InternalListEnrollmentsResponseItem)


/**
 * The thresholds of the enrollments, for the reidentify job of the metadata service. The job must score with the same thresholds as the servers, or it would change verdicts that operators already saw.
 * @summary Get enrollment settings
 */
export const InternalGetEnrollmentSettingsQueryParams = zod.object({
  "orgId": zod.string().optional()
})

export const InternalGetEnrollmentSettingsResponse = EnrollmentSettingsResponse


/**
 * One enrollment, for the reidentify job of the metadata service.
 * @summary Get enrollment
 */
export const InternalGetEnrollmentParams = zod.object({
  "id": zod.string()
})

export const InternalGetEnrollmentQueryParams = zod.object({
  "orgId": zod.string().optional(),
  "withEmbeddings": zod.boolean().optional()
})

export const InternalGetEnrollmentResponse = EnrollmentDetail


/**
 * @summary Get archive encryption
 */
export const GetArchiveEncryptionQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetArchiveEncryptionResponse = zod.object({
  "enabled": zod.boolean(),
  "hasSavedKey": zod.boolean()
})


/**
 * @summary Set archive encryption key
 */
export const SetArchiveEncryptionKeyQueryParams = zod.object({
  "orgId": zod.string()
})

export const SetArchiveEncryptionKeyBody = SetKeyRequest

export const SetArchiveEncryptionKeyResponse = zod.object({
  "keyId": zod.string()
})


/**
 * @summary Delete archive encryption key
 */
export const DeleteArchiveEncryptionKeyParams = zod.object({
  "keyId": zod.string()
})

export const DeleteArchiveEncryptionKeyQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteArchiveEncryptionKeyResponse = zod.object({
  "deleted": zod.boolean()
})


/**
 * @summary Enable archive encryption
 */
export const EnableArchiveEncryptionQueryParams = zod.object({
  "orgId": zod.string()
})

export const EnableArchiveEncryptionResponse = zod.object({
  "enabled": zod.boolean()
})


/**
 * @summary Disable archive encryption
 */
export const DisableArchiveEncryptionQueryParams = zod.object({
  "orgId": zod.string()
})

export const DisableArchiveEncryptionResponse = zod.object({
  "enabled": zod.boolean()
})


/**
 * @summary Unlock archive encryption
 */
export const UnlockArchiveEncryptionQueryParams = zod.object({
  "orgId": zod.string()
})

export const UnlockArchiveEncryptionBody = UnlockRequest

export const UnlockArchiveEncryptionResponse = zod.object({
  "keyId": zod.string()
})


/**
 * @summary List layouts
 */
export const ListLayoutsQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListLayoutsResponseItem = Layout
export const ListLayoutsResponse = zod.array(ListLayoutsResponseItem)


/**
 * @summary Create layout
 */
export const CreateLayoutQueryParams = zod.object({
  "orgId": zod.string()
})

export const CreateLayoutBody = LayoutInput

export const CreateLayoutResponse = Layout


/**
 * @summary Get layout
 */
export const GetLayoutParams = zod.object({
  "id": zod.string()
})

export const GetLayoutQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetLayoutResponse = Layout


/**
 * @summary Update layout
 */
export const UpdateLayoutParams = zod.object({
  "id": zod.string()
})

export const UpdateLayoutQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateLayoutBody = LayoutInput

export const UpdateLayoutResponse = Layout


/**
 * @summary Delete layout
 */
export const DeleteLayoutParams = zod.object({
  "id": zod.string()
})

export const DeleteLayoutQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteLayoutResponse = zod.void()


/**
 * @summary Get stored file
 */
export const GetStoredFileQueryParams = zod.object({
  "key": zod.string().optional(),
  "name": zod.string().optional(),
  "type": zod.string().optional(),
  "exp": zod.string().optional(),
  "sig": zod.string().optional()
})

export const GetStoredFileResponse = zod.unknown()


/**
 * @summary List cases
 */
export const ListCasesQueryParams = zod.object({
  "orgId": zod.string(),
  "status": zod.string().optional().describe('The statuses of the cases to return, separated by `|`, such as `open|review`. Without it, cases of every status.')
})

export const ListCasesResponseItem = CaseDetail
export const ListCasesResponse = zod.array(ListCasesResponseItem)


/**
 * @summary Create case
 */
export const CreateCaseQueryParams = zod.object({
  "orgId": zod.string()
})

export const CreateCaseBody = CaseInput

export const CreateCaseResponse = Case


/**
 * @summary Get case
 */
export const GetCaseParams = zod.object({
  "id": zod.string()
})

export const GetCaseQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetCaseResponse = CaseDetail


/**
 * @summary Update case
 */
export const UpdateCaseParams = zod.object({
  "id": zod.string()
})

export const UpdateCaseQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateCaseBody = CaseInput

export const UpdateCaseResponse = Case


/**
 * @summary Delete case
 */
export const DeleteCaseParams = zod.object({
  "id": zod.string()
})

export const DeleteCaseQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteCaseResponse = zod.void()


/**
 * @summary Attach case bookmark
 */
export const AttachCaseBookmarkParams = zod.object({
  "id": zod.string()
})

export const AttachCaseBookmarkQueryParams = zod.object({
  "orgId": zod.string()
})

export const AttachCaseBookmarkBody = zod.object({
  "bookmarkId": zod.string().nullish()
})

export const AttachCaseBookmarkResponse = Bookmark


/**
 * @summary Detach case bookmark
 */
export const DetachCaseBookmarkParams = zod.object({
  "id": zod.string(),
  "bid": zod.string()
})

export const DetachCaseBookmarkQueryParams = zod.object({
  "orgId": zod.string()
})

export const DetachCaseBookmarkResponse = zod.void()


/**
 * @summary Upload case attachment
 */
export const UploadCaseAttachmentParams = zod.object({
  "id": zod.string()
})

export const UploadCaseAttachmentQueryParams = zod.object({
  "orgId": zod.string()
})

export const UploadCaseAttachmentBody = zod.object({
  "name": zod.string().optional(),
  "note": zod.string().optional(),
  "file": zod.instanceof(Blob).optional()
})

export const UploadCaseAttachmentResponse = CaseAttachment


/**
 * @summary Get case attachment content
 */
export const GetCaseAttachmentContentParams = zod.object({
  "id": zod.string(),
  "aid": zod.string()
})

export const GetCaseAttachmentContentQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetCaseAttachmentContentResponse = zod.object({
  "url": zod.string().describe('Relative on a deployment that keeps files on its own disk.'),
  "expiresAt": zod.iso.datetime({"offset":true})
})


/**
 * @summary Update case attachment
 */
export const UpdateCaseAttachmentParams = zod.object({
  "id": zod.string(),
  "aid": zod.string()
})

export const UpdateCaseAttachmentQueryParams = zod.object({
  "orgId": zod.string()
})

export const UpdateCaseAttachmentBody = zod.object({
  "name": zod.string().nullish(),
  "note": zod.string().nullish(),
  "position": zod.int().nullish()
})

export const UpdateCaseAttachmentResponse = CaseAttachment


/**
 * @summary Delete case attachment
 */
export const DeleteCaseAttachmentParams = zod.object({
  "id": zod.string(),
  "aid": zod.string()
})

export const DeleteCaseAttachmentQueryParams = zod.object({
  "orgId": zod.string()
})

export const DeleteCaseAttachmentResponse = zod.void()


/**
 * @summary List changelog
 */
export const listChangelogQueryLimitMax = 1000;



export const ListChangelogQueryParams = zod.object({
  "orgId": zod.string(),
  "serverId": zod.string(),
  "after": zod.int().optional().describe('The seq after which the entries are returned; zero or none for all of them.'),
  "limit": zod.int().min(1).max(listChangelogQueryLimitMax).optional()
})

export const ListChangelogResponseItem = ChangeEvent
export const ListChangelogResponse = zod.array(ListChangelogResponseItem)


/**
 * @summary List audit
 */
export const listAuditQueryLimitMax = 1000;



export const ListAuditQueryParams = zod.object({
  "orgId": zod.string(),
  "actor": zod.string().optional(),
  "session": zod.string().optional().describe('The id of a login session, whose records to return.'),
  "resource": zod.string().optional().describe('The id of one object, a rule or a camera. A record matches when it is about that object.'),
  "before": zod.int().optional().describe('The seq of the last record of the previous page; zero or none for the first page.'),
  "limit": zod.int().min(1).max(listAuditQueryLimitMax).optional(),
  "from": zod.iso.datetime({"offset":true}).optional().describe('The lower bound of the event time.'),
  "to": zod.iso.datetime({"offset":true}).optional().describe('The upper bound of the event time.'),
  "types": zod.array(zod.string()).optional().describe('The record types, as a comma separated list, the parameter repeated, or both.'),
  "devices": zod.array(zod.string()).optional().describe('Devices, as a comma separated list, the parameter repeated, or both. A record matches when it is about any of them.')
})

export const ListAuditResponseItem = AuditRecord
export const ListAuditResponse = zod.array(ListAuditResponseItem)


/**
 * @summary Ingest audit
 */
export const IngestAuditQueryParams = zod.object({
  "orgId": zod.string()
})

export const IngestAuditBodyItem = AuditRecordInput
export const IngestAuditBody = zod.array(IngestAuditBodyItem)

export const IngestAuditResponse = zod.object({
  "stored": zod.int()
})


/**
 * @summary Record chat audit
 */
export const InternalRecordChatAuditBody = ChatRequest

export const InternalRecordChatAuditResponse = zod.void()


/**
 * @summary List health log
 */
export const listHealthLogQueryLimitMax = 1000;



export const ListHealthLogQueryParams = zod.object({
  "orgId": zod.string(),
  "severity": zod.string().optional(),
  "serverId": zod.string().optional().describe('The media server or toolbox that reported the records.'),
  "before": zod.int().optional().describe('The seq of the last record of the previous page; zero or none for the first page.'),
  "after": zod.int().optional().describe('The seq of the first record of the next page, for the page back from it.'),
  "limit": zod.int().min(1).max(listHealthLogQueryLimitMax).optional(),
  "from": zod.iso.datetime({"offset":true}).optional().describe('The lower bound of when the records happened.'),
  "to": zod.iso.datetime({"offset":true}).optional().describe('The upper bound of when the records happened.'),
  "type": zod.array(zod.string()).optional().describe('A problem, such as `cameraOffline`. Repeated, the records of any of them are returned.')
})

export const ListHealthLogResponseItem = HealthMessage
export const ListHealthLogResponse = zod.array(ListHealthLogResponseItem)


/**
 * @summary Ingest health log
 */
export const IngestHealthLogQueryParams = zod.object({
  "orgId": zod.string()
})

export const IngestHealthLogBodyItem = HealthMessageInput
export const IngestHealthLogBody = zod.array(IngestHealthLogBodyItem)

export const IngestHealthLogResponse = zod.object({
  "stored": zod.int()
})


/**
 * @summary List update status
 */
export const ListUpdateStatusQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListUpdateStatusResponseItem = ServerUpdateStatus
export const ListUpdateStatusResponse = zod.array(ListUpdateStatusResponseItem)


/**
 * @summary Get update status summary
 */
export const GetUpdateStatusSummaryQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetUpdateStatusSummaryResponseItem = LocationUpdateSummary
export const GetUpdateStatusSummaryResponse = zod.array(GetUpdateStatusSummaryResponseItem)


/**
 * @summary List available updates
 */
export const ListAvailableUpdatesQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListAvailableUpdatesResponse = AvailableUpdates


/**
 * @summary List update plans
 */
export const ListUpdatePlansQueryParams = zod.object({
  "orgId": zod.string()
})

export const ListUpdatePlansResponseItem = UpdatePlan
export const ListUpdatePlansResponse = zod.array(ListUpdatePlansResponseItem)


/**
 * @summary Create update plan
 */
export const CreateUpdatePlanQueryParams = zod.object({
  "orgId": zod.string()
})

export const CreateUpdatePlanBody = CreateUpdatePlanRequest

export const CreateUpdatePlanResponse = UpdatePlan


/**
 * Opens the organization's plan for a private build. The cloud reads the manifest that CI published under the key of the build. A private build is not in the build registry. 404 is an unknown key, 409 is a second open plan, 422 is an organization without servers, and 502 is a manifest that the cloud cannot read or that is not valid.
 * @summary Create private update plan
 */
export const CreatePrivateUpdatePlanQueryParams = zod.object({
  "orgId": zod.string()
})

export const CreatePrivateUpdatePlanBody = CreatePrivateUpdatePlanRequest

export const CreatePrivateUpdatePlanResponse = UpdatePlan


/**
 * @summary Install update plan now
 */
export const InstallUpdatePlanNowParams = zod.object({
  "id": zod.string()
})

export const InstallUpdatePlanNowQueryParams = zod.object({
  "orgId": zod.string()
})

export const InstallUpdatePlanNowResponse = UpdatePlan


/**
 * @summary Cancel update plan
 */
export const CancelUpdatePlanParams = zod.object({
  "id": zod.string()
})

export const CancelUpdatePlanQueryParams = zod.object({
  "orgId": zod.string()
})

export const CancelUpdatePlanResponse = UpdatePlan


/**
 * @summary Get release manifest
 */
export const GetReleaseManifestResponse = ManifestStatus


/**
 * @summary List builds
 */
export const ListBuildsResponseItem = Build
export const ListBuildsResponse = zod.array(ListBuildsResponseItem)


/**
 * @summary Publish build
 */
export const PublishBuildBody = PublishRequest

export const PublishBuildResponse = Build


/**
 * @summary Withdraw build
 */
export const WithdrawBuildParams = zod.object({
  "version": zod.string()
})

export const WithdrawBuildResponse = Build


/**
 * @summary Archive build
 */
export const ArchiveBuildParams = zod.object({
  "version": zod.string()
})

export const ArchiveBuildResponse = Build


/**
 * @summary Restore build
 */
export const RestoreBuildParams = zod.object({
  "version": zod.string()
})

export const RestoreBuildResponse = Build


/**
 * Every release version whose files must stay in the release bucket. The script that prunes the bucket reads it without a user session, so the operation needs no credential.
 * @summary List retained versions
 */
export const ListRetainedVersionsResponse = RetainedVersionsResponse


/**
 * The build that this deployment offers, or a null build when it offers none, as the download page of the landing site shows it. The page reads it from the browser on the site's own origin, so the operation needs no credential and answers that origin's CORS.
 * @summary Get latest build
 */
export const GetLatestBuildResponse = LatestBuildResponse


/**
 * @summary Telegram webhook
 */
export const TelegramWebhookBody = TelegramUpdateInput

export const TelegramWebhookResponse = zod.unknown()


/**
 * @summary Link telegram
 */
export const LinkTelegramQueryParams = zod.object({
  "orgId": zod.string()
})

export const LinkTelegramResponse = TelegramLink


/**
 * @summary Unlink telegram
 */
export const UnlinkTelegramQueryParams = zod.object({
  "orgId": zod.string()
})

export const UnlinkTelegramResponse = zod.void()


/**
 * @summary Get telegram status
 */
export const GetTelegramStatusQueryParams = zod.object({
  "orgId": zod.string()
})

export const GetTelegramStatusResponse = TelegramStatus


/**
 * @summary List relay servers
 */
export const ListRelayServersResponseItem = zod.string()
export const ListRelayServersResponse = zod.array(ListRelayServersResponseItem)


/**
 * @summary Get TURN credentials
 */
export const GetTurnCredentialsResponse = TurnCredentials
